<template>
  <div class="terms-of-use__wrapper">
    <div class="terms-of-use">
      <div class="terms-of-use__head-wrapper">
        <div class="terms-of-use__head">
          <div class="bold-600 fs-36 mb-3">
            Terms of use
          </div>
          <div class="bold fs-18">
            Effective Date: May 08, 2026 <br>
            For: SatoshiEye
          </div>
        </div>
      </div>

      <div class="terms-of-use__content-wrapper">
        <div class="terms-of-use__content">
          <div class="bold-600 fs-24 mb-3">
            Terms and Definitions
          </div>
          <p>
            For the purposes of these Terms of Service (hereinafter the
            "Terms"), the following terms shall have the meanings set forth
            below:
          </p>
          <ul>
            <li>
              "Account" means the registered account through which a Customer
              accesses the Services.
            </li>
            <li>
              "Billing Period" means the recurring period for which a
              Subscription is purchased, as stated at checkout or in an Order
              Form (for example, monthly or annually).
            </li>
            <li>
              "Confidential Information" means any and all information received
              or acquired by Customer from SatoshiEye in pursuance of these
              Terms, in whatever form, directly or indirectly, orally or in
              writing, that is designated as confidential or that is reasonably
              expected to be confidential given the nature of the information
              and the circumstances of disclosure.
            </li>
            <li>
              "Customer" or "you" means the legal entity/person that is
              purchasing access to the SatoshiEye service.
            </li>
            <li>
              "Device" means a computer or a mobile device owned or otherwise
              controlled by Customer and/or Customer's staff where the Services
              are used by means of a Browser.
            </li>
            <li>
              "Included Usage" means the quantity of Reports included in the
              applicable Subscription Plan for a given Billing Period.
            </li>
            <li>
              "Intellectual Property Rights" means patents, copyrights,
              registered and unregistered design rights, trademarks, trade
              secrets, know-how, database rights, and all other similar or
              corresponding proprietary rights (whether registered or
              unregistered) and all applications for the same, anywhere in the
              world.
            </li>
            <li>
              "Order Form" means any order page, checkout page, plan
              confirmation, invoice, or other written ordering document
              describing the Subscription Plan, pricing, Included Usage, and
              Billing Period.
            </li>
            <li>
              "Payment Method" means a valid current payment method accepted by
              SatoshiEye and linked to the Customer's Account.
            </li>
            <li>
              "Renewal Date" means the date on which a Subscription renews for a
              new Billing Period.
            </li>
            <li>
              "Report" means the information provided to the Customer concerning
              the AML risk assessment of blockchain addresses associated with
              virtual assets. The Report may come in various types, each
              contingent on its content. The content of each Report type may be
              subject to alteration at the sole discretion of SatoshiEye.
              SatoshiEye does not provide any explicit or implicit assurance
              that the Report will align with the Customer's requirements.
            </li>
            <li>
              "SatoshiEye" means a web application operating on the internet and
              accessible for use by means of web browsing software (such as
              Google Chrome, Microsoft Edge, Mozilla Firefox, or other
              compatible software, hereinafter "Browser") and, if applicable,
              the SatoshiEye API.
            </li>
            <li>
              "Services" means the SatoshiEye web application, reports,
              monitoring tools, data products, and, if applicable, the
              SatoshiEye API and related services made available by SatoshiEye.
            </li>
            <li>
              "Subscription" means a recurring paid right to access and use the
              Services during a Billing Period, subject to these Terms.
            </li>
            <li>
              "Subscription Fee" means the recurring fee payable for the
              applicable Subscription Plan.
            </li>
            <li>
              "Updates" means any updates, upgrades, bug fixes, patches or other
              error corrections to the Services provided by SatoshiEye.
            </li>
            <li>
              "Usage Cycle" means the period over which report quotas, refresh
              allowances, API usage, or other Included Usage are measured and
              refreshed for the applicable Subscription Plan, as described on
              the Website, at checkout, or in the Customer’s Account. A Usage
              Cycle may differ from the Billing Period.
            </li>
          </ul>

          <div class="bold-600 fs-24 mb-3">
            Terms of Service
          </div>
          <p>
            These Terms constitute a legal agreement between Customer ("you")
            and SatoshiEye (a product of GLO Services AG, incorporated and
            registered in Switzerland with company number CHE-440.055.156 whose
            registered office is at Grafenauweg 8, CH-6300 Zug, Switzerland) and
            governs your use of the Services accessible through a Browser,
            including any updates or supplements to the Services, subject to any
            additional terms that might apply to such updates or supplements.
            These Terms are made by providing Customer's full and unconditional
            consent to conclude the Terms in full, without signing a written
            copy of the Terms by the Parties. These Terms have full legal force
            and effect and are equivalent to the Terms signed by the Parties in
            writing.
          </p>
          <p>
            The Services are protected by SatoshiEye's, or its or their
            respective licensors or service providers' Intellectual Property
            Rights.
          </p>
          <p>
            The terms of our website, Privacy Policy and Cookie Policy which are
            available at
            <a
              :href="websiteUrl"
              rel="noopener noreferrer"
              target="_blank"
            >https://satoshieye.com</a>
            ("Website"), are incorporated into these Terms by reference.
          </p>
          <p>
            These Terms represent the entire agreement concerning the Services
            between you and SatoshiEye, and it supersedes any prior proposal,
            representation, or understanding between the parties.
          </p>
          <p>
            By agreeing and/or using the Services you (a) acknowledge that you
            have read and understood these Terms; (b) accept these Terms and
            agree that you are legally bound by its terms and automatically
            agree for prices for Services and all annexes that are integral
            parts of these Terms; and (c) represent and warrant that you have
            authority to bind Customer to these Terms if Customer is a legal
            entity.
          </p>
          <p>If you do not agree to these terms, do not use the Services.</p>
          <p>
            SatoshiEye may update these Terms from time to time. Material
            changes affecting fees, core service features, or your rights will
            be notified in advance where required by applicable law. If
            SatoshiEye modifies these Terms, SatoshiEye will post the
            modification at
            <a
              :href="websiteUrl"
              rel="noopener noreferrer"
              target="_blank"
            >https://satoshieye.com</a>. SatoshiEye will endeavour to notify you of any changes by email
            but will not be liable for any failure to do so. SatoshiEye will
            also update the "Effective Date" at the top of these Terms.
          </p>
          <p>
            If you continue to use the Services after updated Terms take effect,
            the updated Terms will apply to your continued use, subject to
            applicable law. If the modified Terms are not acceptable to you,
            please do not access or use the Services.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Access and Use of Services
          </div>
          <p>
            Subject to the terms of these Terms and your payments in accordance
            with the Payment Terms section of these Terms, SatoshiEye grants you
            a limited, non-exclusive, non-transferable, and revocable right to
            access and use the Services strictly in accordance with the
            Services' documentation and these Terms.
          </p>
          <p>
            You may access the Services and view, use, and display the Services
            on your Device for your personal use or internal business purposes
            only (which shall not include the use of the Services by, or for the
            benefit of, any person other than you or your business).
          </p>
          <p>
            The Customer is responsible for maintaining the confidentiality of
            the Account credentials and for all activities that occur under the
            Account. The Customer shall promptly notify SatoshiEye of any
            unauthorized access to or use of the Account.
          </p>
          <p>
            The Services are not intended for individuals under the age of 18.
            SatoshiEye does not knowingly engage in marketing communications
            directed at minors.
          </p>

          <div class="mb-3">
            Personalisation
          </div>
          <p>
            Subject to applicable law and, where required, your consent,
            SatoshiEye may use information about your interactions with the
            Services to improve user experience, recommend relevant features,
            and tailor communications.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Subscription Model; Transition from Legacy Lookup Model
          </div>
          <p>
            From May 08, 2026, SatoshiEye no longer offers Lookups, top-ups,
            prepaid packages, synthetic tokens, or similar prepaid balances for
            new purchases. New paid access to the Services is offered through
            subscription plans billed to the Customer’s selected Payment Method.
          </p>
          <p>
            Reports purchased before the Effective Date under the legacy Lookup
            model will remain identified in the Customer’s Account as legacy
            reports. Unless expressly stated otherwise by SatoshiEye, legacy
            reports do not create any right to convert prior token balances into
            cash, credits, stored value, or subscription entitlements.
          </p>
          <p>
            SatoshiEye may, at its sole discretion, offer existing registered
            Customers a promotional code for a discount for one month "Standard"
            subscription when transitioning to a subscription model. Any such
            offer will be governed by the specific terms presented to registered
            Customers via email.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Subscription Plans and Included Usage
          </div>
          <p>
            SatoshiEye offers subscription plans as described on the Website
            and, where applicable, in an Order Form. Each Subscription Plan will
            state the applicable Subscription Fee, Billing Period, Usage Cycle,
            Report quota or other Included Usage, available features, and any
            plan-specific limitations.
          </p>
          <p>
            Included Usage is a contractual usage allowance. It is not money,
            stored value, property, or a separate product, has no cash value,
            and may be used only during an active Subscription.
          </p>
          <p>
            Unless otherwise stated on the Website, at checkout, or in the
            Customer’s Account, unused Included Usage expires at the end of the
            relevant Usage Cycle and does not roll over.
          </p>
          <p>
            If the Customer exhausts the Included Usage available under the
            applicable Subscription Plan, SatoshiEye may, as described on the
            Website, at checkout, or in the Customer’s Account, permit the
            Customer to: (a) wait until the next Usage Cycle begins and the
            Included Usage refreshes automatically; (b) upgrade to another
            Subscription Plan; or (c) purchase additional Report quota or
            another add-on made available by SatoshiEye.
          </p>
          <p>
            If a Customer upgrades from one monthly Subscription Plan to another
            monthly Subscription Plan, the Subscription Fee for the new plan
            will be charged in full at the time of upgrade, the new quota will
            apply immediately, and the next billing date will reset to the date
            of that upgrade unless otherwise stated at checkout.
          </p>
          <p>
            If a Customer with an annual Subscription Plan purchases an
            additional monthly quota package or similar add-on, the applicable
            add-on fee will be charged when purchased, the additional quota will
            apply immediately, and the annual subscription end date will not
            change unless otherwise stated at checkout.
          </p>
          <p>
            SatoshiEye may modify the features, Included Usage thresholds,
            technical specifications, and plan structure of Subscription Plans
            from time to time. Any material change to a paid Subscription Plan
            will take effect in accordance with the "Changes to Subscription
            Fees and Plans" section below.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Payment Terms
          </div>
          <p>
            The Subscription Fee, Billing Period, Included Usage, and any
            applicable overage pricing will be shown at checkout or in an Order
            Form.
          </p>
          <p>
            All amounts shall be expressed and made in Euro, unless otherwise
            specified.
          </p>
          <p>Unless otherwise stated at checkout or in an Order Form:</p>
          <p>
            (a) prices shown to Customers include taxes that SatoshiEye is
            required to collect in connection with the Subscription; and
            <br>(b) prices shown to Business Customers are exclusive of VAT,
            GST, sales tax, withholding tax, or similar indirect taxes.
          </p>
          <p>
            The Customer is responsible for all taxes, duties, levies, or
            similar governmental assessments arising from the purchase or use of
            the Services, excluding taxes based on SatoshiEye’s net income,
            property, or employees.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Billing Authorization and Automatic Renewal
          </div>
          <p>
            By starting a Subscription, the Customer expressly authorizes
            SatoshiEye and its payment service providers to charge the Payment
            Method for: (a) the initial Subscription Fee; (b) each recurring
            renewal Subscription Fee; (c) any applicable taxes; and (d) any
            other amounts expressly payable under these Terms or an Order Form,
            until the Subscription is cancelled or terminated in accordance with
            these Terms.
          </p>
          <p>
            Unless otherwise stated at checkout or in an Order Form, each
            Subscription renews automatically for successive Billing Periods of
            the same duration as the initial Billing Period. SatoshiEye will
            charge the Payment Method on or about the first day of each renewal
            Billing Period.
          </p>
          <p>
            The Customer must keep the Payment Method, billing address, tax
            information, and contact details associated with the Account
            accurate and up to date at all times.
          </p>
          <p>
            After each successful payment, SatoshiEye may send the Customer an
            invoice, receipt, or similar billing confirmation to the email
            address associated with the Account.
          </p>
          <p>
            SatoshiEye may also send or display in the Account any renewal
            notices, cancellation confirmations, payment-failure notices,
            trial-end notices, or other communications required by applicable
            law, card-network rules, or SatoshiEye’s operational policies.
          </p>

          <div class="bold-600 fs-16 mb-3">
            Marketing Communications
          </div>
          <p>
            SatoshiEye may send you communications relating to the Services,
            including product updates, educational content, and promotional
            offers:
          </p>
          <p>
            (a) where you have provided your consent; or <br>(b) where
            permitted under applicable law, including where you are an existing
            customer and such communications relate to services similar to those
            previously purchased.
          </p>
          <p>
            You may opt out of marketing communications at any time by using the
            personal unsubscribe link provided in the most recent marketing
            email you have received from us, or by contacting us directly.
          </p>
          <p>
            SatoshiEye will comply with applicable laws governing electronic
            marketing communications in the jurisdictions in which such
            communications are sent.
          </p>
          <p>
            Where Customer is a legal entity, SatoshiEye may contact Customer
            representatives with information about its services based on its
            legitimate interests, in accordance with applicable law. Recipients
            may object to such communications at any time.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Free Trials, Introductory Offers, and Promotions
          </div>
          <p>
            If SatoshiEye offers a 7-day free trial, discounted introductory
            period, or other promotional pricing, the duration of the offer, the
            date of the first paid charge, the recurring Subscription Fee after
            the offer ends, and the applicable Billing Period will be disclosed
            at checkout and in the enrollment confirmation.
          </p>
          <p>
            Unless the Customer cancels before the end of the free 7-day trial,
            or 1-month promotional period, the Customer authorizes SatoshiEye to
            charge the Payment Method at the recurring Subscription Fee
            disclosed at checkout when the offer period ends and on each
            subsequent Renewal Date.
          </p>
          <p>
            SatoshiEye may determine eligibility for any free trial,
            introductory offer, or promotion in its sole discretion and may
            modify or withdraw promotions at any time to the extent permitted by
            applicable law.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Changes to Subscription Fees and Plans
          </div>
          <p>
            SatoshiEye may change Subscription Fees, Included Usage limits,
            Usage Cycles, plan features, or Billing Period options from time to
            time.
          </p>
          <p>
            SatoshiEye will provide at least 30 days’ prior notice of any
            material increase in the recurring Subscription Fee or any material
            change to the Billing Period, and such change will take effect no
            earlier than the next Renewal Date following the notice period,
            unless a different period is required or permitted by applicable law
            or card-network rules.
          </p>
          <p>
            If the Customer does not agree to the change, the Customer may
            cancel the Subscription before the change takes effect. If the
            Customer does not cancel and the Subscription renews after the
            effective date of the change, the renewed Subscription will be
            subject to the updated pricing or plan terms.
          </p>

          <div class="bold-600 fs-16 mb-3">
            Upgrading Your Plan
          </div>
          <p>
            Payment: You pay the Full Price of the new plan immediately. Any
            remaining days or reports from your old plan are discarded.
          </p>
          <p>
            Billing Cycle: Your billing month "restarts" today. Your next
            payment will be scheduled exactly one month from now.
          </p>
          <p>
            Reports: Your old report balance is cleared, and you receive the
            full quota of your new plan instantly.
          </p>
          <p>
            Access: Higher-tier features are unlocked the moment the payment is
            successful.
          </p>

          <div class="bold-600 fs-16 mb-3">
            Switching from Monthly to Annual
          </div>
          <p>Payment: You pay the Full Price of the Annual Plan today.</p>
          <p>Billing Cycle: Your new "Yearly Anniversary" starts today.</p>
          <p>
            Reports: You receive a fresh quota immediately. This quota will
            automatically refill every month on the same day of the month you
            upgraded.
          </p>

          <div class="bold-600 fs-16 mb-3">
            Lowering Your Plan
          </div>
          <p>
            Payment: You do not pay anything at the moment you request a
            downgrade. No refunds are issued for the current period.
          </p>
          <p>
            Access: You keep full access to your current (higher) plan and all
            its reports until the end of your current paid period.
          </p>
          <p>
            The Switch: The system only switches you to the lower plan and
            charges the lower price when your current month or year expires.
          </p>

          <div class="bold-600 fs-16 mb-3">
            Switching from Annual to Monthly
          </div>
          <p>
            You remain on the Annual plan with all its benefits until the year
            is up.
          </p>
          <p>
            On the anniversary date, instead of charging you for another year,
            the system will switch you to the monthly rate.
          </p>

          <div class="bold-600 fs-16 mb-3">
            Upgrading during Trial
          </div>
          <p>For users who want to try a higher tier before the trial ends.</p>
          <p>Payment: No immediate charge. You stay in "Trial Mode."</p>
          <p>
            The Deal: You are essentially telling the system: "On Day 8, please
            charge me for Pro instead of Standard."
          </p>
          <p>
            Access: Higher-tier features (like Pro modules) are unlocked
            immediately so you can test them.
          </p>
          <p>
            Trial Clock: The 7-day timer does not restart. If you upgrade on Day
            4, you still only have 3 days left of the free trial.
          </p>
          <p>
            Reports: You do not get extra reports. You keep the remaining
            balance of your original trial quota.
          </p>

          <div class="bold-600 fs-16 mb-3">
            Downgrading during Trial
          </div>
          <p>
            Trial Ends Instantly: The free trial is terminated immediately the
            moment you confirm the downgrade.
          </p>
          <p>
            Immediate Payment: You are charged the full price of the new lower
            plan right away.
          </p>
          <p>New Start Date: Today becomes your new "Billing Anniversary."</p>
          <p>
            Reports &amp; Access: Trial-only "Pro" features are locked
            instantly. Your report quota is reset to the full amount of the new
            paid plan (e.g., you move from 5 trial reports to 75 paid reports).
          </p>

          <div class="bold-600 fs-24 mb-3">
            Failed Payments; Temporary Restriction; Cancellation
          </div>
          <p>
            If any charge to the Payment Method is declined, reversed, or
            unpaid, SatoshiEye may: re-attempt the charge and apply the
            following standard service restrictions:
          </p>
          <p>
            (a) from Day 0 through Day 3 after the payment failure, the Customer
            may continue to view and download reports generated before the
            payment failure, but may not generate new reports or request data
            refreshes;
          </p>
          <p>
            (b) from Day 4 through Day 7 after the payment failure, access to
            all paid features, including previously generated paid reports and
            archives, may be suspended; and
          </p>
          <p>
            (c) if payment is not successfully collected by Day 8, SatoshiEye
            may cancel the affected Subscription.
          </p>
          <p>
            SatoshiEye may require the Customer to update the Payment Method at
            any time during this period.
          </p>
          <p>
            SatoshiEye may temporarily restrict or cancel the Account or
            Subscription immediately if the Customer materially breaches these
            Terms, engages in fraud, abuse, prohibited activity, or illegal use,
            or if continued provision of the Services would create legal,
            regulatory, security, or operational risk.
          </p>
          <p>
            Cancellation or temporary restriction does not relieve the Customer
            of any payment obligation accrued before the effective date of the
            cancellation or temporary restriction.
          </p>
          <p>Access is restored automatically after successful payment.</p>

          <div class="bold-600 fs-24 mb-3">
            Cancellation by Customer; Effect of Cancellation
          </div>
          <p>
            The Customer may cancel a Subscription at any time through the
            subscription settings in the Account.
          </p>
          <p>
            Unless otherwise expressly stated in an Order Form or required by
            applicable law, cancellation takes effect at the end of the
            then-current Billing Period. The Customer will continue to have
            access to the paid Subscription features until the effective
            cancellation date, and SatoshiEye will not charge any future renewal
            Subscription Fee after that date.
          </p>
          <p>
            Cancellation of a recurring card payment through the Customer’s card
            issuer or bank stops the payment authority to that issuer or bank
            but does not by itself terminate the Subscription agreement with
            SatoshiEye. To terminate the Subscription agreement, the Customer
            must also cancel the Subscription through the Account or another
            cancellation method made available by SatoshiEye.
          </p>
          <p>
            Except as expressly stated in these Terms or required by applicable
            law, fees already paid for the current Billing Period are
            non-refundable and non-creditable, and SatoshiEye does not provide
            refunds or credits for partial Billing Periods, unused time, or
            unused Included Usage.
          </p>
          <p>
            If SatoshiEye terminates a Subscription for convenience, SatoshiEye
            may provide a pro-rated refund for the unused portion of the
            then-current paid term, except where the termination results from
            the Customer’s breach, fraud, unlawful conduct, non-payment, or
            legal/regulatory necessity.
          </p>
          <p>
            Upon expiration or termination of the Subscription, the Customer’s
            right to access paid features ends on the effective date. Stored
            reports remain accessible only while the Subscription is active.
          </p>
          <p>
            SatoshiEye may retain certain account, billing, security,
            fraud-prevention, and transaction records after termination where
            necessary for legal, accounting, dispute resolution, security, or
            regulatory purposes, in accordance with the
            <a
              :href="privacyPolicyUrl"
              rel="noopener noreferrer"
              target="_blank"
            >Privacy Policy</a>.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Reports and Data Refreshes
          </div>
          <p>
            SatoshiEye offers subscription plans with plan-specific report
            content, refresh allowances, and usage limits as described on the
            Website, in the Customer’s Account, or at checkout.
          </p>
          <p>As of the Effective Date, the Subscription Plan tiers are:</p>
          <p>(a) Starter: Risk Score;</p>
          <p>
            (b) Standard: Risk Score, Money Trail, and Funds Breakdown by Risk
            Category; and
          </p>
          <p>
            (c) Pro: Risk Score, Money Trail, Funds Breakdown by Risk Category,
            Full Transaction Ledger, and Reveal Specific Entities.
          </p>
          <p>
            SatoshiEye may update the names, presentation, fields, contents, and
            structure of Reports from time to time.
          </p>
          <p>
            A report generation or a data refresh may consume Included Usage
            according to the rules applicable to the selected plan. Unless
            otherwise stated on the Website, at checkout, or in the Customer’s
            Account:
          </p>
          <p>(a) under Starter, each data refresh consumes one Report quota;</p>
          <p>
            (b) under Standard, each unique report includes two free data
            refreshes, after which each further refresh consumes one report
            quota; and
          </p>
          <p>
            (c) under Pro, data refreshes are unlimited during an active
            Subscription, subject to fair use, anti-abuse controls, and
            reasonable technical limitations.
          </p>
          <p>
            Reports and data refreshes are available only while the applicable
            Subscription remains active. When the Subscription ends or is
            cancelled, access to paid Reports and related outputs may be
            blocked, removed, or otherwise restricted.
          </p>
          <p>
            For the purposes of these Terms, a "Unique Report" means the first
            report generated for a specific wallet address or other identifier
            during an active Subscription, excluding later refreshes of that
            same report.
          </p>

          <div class="bold-600 fs-16 mb-3">
            Automated Processing and Profiling
          </div>
          <p>
            The Services use automated processing of blockchain-related data to
            generate risk scores, analytics, and reports.
          </p>
          <p>
            These outputs are based on algorithmic models and available data
            sources and are provided for informational purposes only. They do
            not constitute legal, financial, compliance, investment, or other
            professional advice.
          </p>
          <p>
            Certain processing carried out through the Services may involve
            profiling or similar automated analysis under applicable data
            protection laws.
          </p>
          <p>
            SatoshiEye does not make decisions producing legal effects or
            similarly significant effects concerning individuals solely on the
            basis of automated processing.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Lawful Use and Restrictions on Use
          </div>
          <p>
            Customer represents that it has all rights, notices, permissions,
            and lawful bases necessary to submit any wallet addresses,
            identifiers, or other data to the Services, and will use the
            Services only in compliance with applicable law and its own
            policies.
          </p>
          <p>
            Except as expressly set out in these Terms, or as permitted by
            applicable local law, you shall not:
          </p>
          <ul>
            <li>
              modify, translate, adapt or otherwise create derivative works or
              improvements, whether or not patentable, of the Services;
            </li>
            <li>
              reverse engineer, disassemble, decompile, decode or otherwise
              attempt to derive or gain access to the source code of the
              Services or any part thereof;
            </li>
            <li>
              remove, delete, alter or obscure any trademarks or any copyright,
              patent or other Intellectual Property Rights or proprietary rights
              notices from the Services, including any copy thereof;
            </li>
            <li>
              rent, lease, lend, sell, sublicense, assign, distribute, publish,
              transfer or otherwise make available the Services or any features
              or functionality of the Services, to any third party for any
              reason, including by making the Services available on a network
              where it is capable of being accessed by more than one computer or
              device at any time, or for any services which may benefit the
              third-party business;
            </li>
            <li>
              take any action that imposes an unreasonable or disproportionately
              large load on the Services;
            </li>
            <li>
              facilitate any viruses, trojan horses, malware, worms or other
              computer programming routines that attempt to or may damage,
              disrupt, corrupt, misuse, detrimentally interfere with,
              surreptitiously intercept or expropriate, or gain unauthorized
              access to any system, data or information related to the Services;
            </li>
            <li>
              remove, disable, circumvent, or otherwise create or implement any
              workaround to any copy protection, rights management, or security
              features in or protecting the Services;
            </li>
            <li>
              use the Services in any unlawful manner, for any unlawful purpose,
              or in any manner inconsistent with these terms, or act
              fraudulently or maliciously, for example, by hacking into or
              inserting malicious code, such as viruses, or harmful data, into
              the Services or any operating system;
            </li>
            <li>
              infringe SatoshiEye's Intellectual Property Rights or those of any
              third party in relation to your use of the Services, including by
              the submission of any infringing material;
            </li>
            <li>
              transmit any material that is defamatory, offensive or otherwise
              objectionable in relation to your use of the Services;
            </li>
            <li>
              use the Services in a way that could damage, disable, overburden,
              impair or compromise our systems or security or interfere with
              other users;
            </li>
            <li>
              collect or harvest any information or data from the Services or
              our systems or attempt to decipher any transmissions to or from
              the servers running any Service;
            </li>
            <li>
              use automated queries, data scraping, or any other means to
              access, collect, or extract data from the Services in a manner
              that exceeds normal usage or places an unreasonable burden on the
              Services' infrastructure.
            </li>
          </ul>

          <div class="bold-600 fs-24 mb-3">
            Intellectual Property Ownership
          </div>
          <p>
            The Services and all worldwide Intellectual Property Rights therein,
            are the exclusive property of SatoshiEye and its licensors. All
            rights in and to the Services not expressly granted to you in these
            Terms are reserved by SatoshiEye and its licensors. You agree that
            you neither own nor acquire any rights in the Services not expressly
            granted in these Terms. SatoshiEye and its licensors retain all
            right, title, and interest in the Services and all derivative works
            thereof, including all related Intellectual Property Rights.
          </p>
          <p>
            You acknowledge that you have no right to have access to the
            Services in source-code form.
          </p>
          <p>
            Except for Customer Data, Feedback, and personal data processed in
            accordance with the
            <a
              :href="privacyPolicyUrl"
              rel="noopener noreferrer"
              target="_blank"
            >Privacy Policy</a>, any materials voluntarily submitted by the Customer through or in
            connection with the Services, including support requests,
            screenshots, technical files, documentation, or similar materials,
            may be used by SatoshiEye on a non-exclusive, worldwide,
            royalty-free, transferable, and sub-licensable basis solely as
            reasonably necessary to operate, support, secure, maintain, improve,
            and provide the Services.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Customer Data
          </div>
          <p>
            Customer may submit wallet addresses, transaction identifiers,
            transaction hashes, and related inputs in connection with the
            Services ("Customer Data").
          </p>
          <p>Customer retains ownership of Customer Data.</p>
          <p>
            Customer grants SatoshiEye a non-exclusive, worldwide, royalty-free
            licence to host, process, analyse, transmit, and use Customer Data
            only as reasonably necessary to:
          </p>
          <p>(a) provide and operate the Services;</p>
          <p>(b) maintain security, prevent abuse, fraud, or misuse;</p>
          <p>(c) troubleshoot, test, maintain, and improve the Services; and</p>
          <p>
            (d) generate aggregated or de-identified analytics, statistics, and
            service insights.
          </p>
          <p>
            Where Customer Data includes personal data, such data will be
            processed in accordance with the
            <a
              :href="privacyPolicyUrl"
              rel="noopener noreferrer"
              target="_blank"
            >Privacy Policy</a>
            and applicable law.
          </p>
          <p>
            SatoshiEye will not use identifiable Customer Data for unrelated
            marketing purposes without the Customer’s consent where required by
            law.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Feedback
          </div>
          <p>
            If the Customer provides suggestions, ideas, enhancement requests,
            recommendations, or other feedback regarding the Services
            ("Feedback"), the Customer grants SatoshiEye a non-exclusive,
            worldwide, perpetual, irrevocable, royalty-free licence to use,
            reproduce, modify, and otherwise exploit that Feedback for any
            lawful purpose, without restriction or obligation to the Customer.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Third-Party Services
          </div>
          <p>
            The Services may integrate with, rely on, or enable access to
            third-party websites, services, or platforms (collectively,
            "Third-Party Services"). SatoshiEye does not control any Third-Party
            Services and shall not be responsible for the content,
            functionality, availability, or security of such Third-Party
            Services. Your use of and interaction with any Third-Party Services,
            including through the Services, is at your own risk and is subject
            to the terms, conditions, and policies of the applicable Third-Party
            Services. SatoshiEye is not responsible for any losses or damages
            incurred as a result of your use of or reliance on any Third-Party
            Services.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Updates
          </div>
          <p>
            SatoshiEye may from time to time in its sole discretion develop and
            provide updates, upgrades, and changes to the Services, which may
            include upgrades, bug fixes, patches and other error corrections
            and/or new features (collectively, including related documentation,
            "Updates"). Updates may also modify or delete in their entirety
            certain features and functionality.
          </p>
          <p>
            You agree that SatoshiEye has no obligation to provide any Updates
            or to continue to provide or enable any particular features or
            functionality.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Disclaimer of Warranties
          </div>
          <p>
            The Services are provided to you "as is" and with all faults and
            defects without warranty of any kind. To the maximum extent
            permitted under applicable law, SatoshiEye, on its own behalf and
            its licensors and service providers, expressly disclaims all
            warranties, whether express, implied, statutory or otherwise, with
            respect to the Services, including all implied warranties of
            merchantability, fitness for a particular purpose, title and
            non-infringement, and warranties that may arise out of course of
            dealing, course of performance, usage or trade practice. Without
            limitation to the foregoing, SatoshiEye provides no warranty or
            undertaking, and makes no representation of any kind that the
            Services will meet your requirements, achieve any intended results,
            be compatible or work with any other software, applications, systems
            or services, operate without interruption, meet any performance or
            reliability standards or be error free or that any errors or defects
            can or will be corrected.
          </p>
          <p>
            Reports are provided for informational purposes only and do not
            constitute legal, tax, compliance, investment, or other professional
            advice. Customer remains solely responsible for its own decisions,
            reviews, and compliance determinations.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Limitation of Liability
          </div>
          <p>
            To the maximum extent permitted by applicable law, in no event shall
            SatoshiEye's aggregate liability arising out of or related to these
            Terms, whether arising out of or related to breach of contract, tort
            (including negligence), or otherwise, exceed the total amounts paid
            to SatoshiEye pursuant to these Terms in the twelve (12) month
            period preceding the event giving rise to the claim.
          </p>
          <p>
            In no event shall SatoshiEye have any liability to the other party
            for any lost profits, revenues, or indirect, special, incidental,
            consequential, cover, or punitive damages, whether an action is in
            contract or tort and regardless of the theory of liability, even if
            a party has been advised of the possibility of such damages. The
            foregoing disclaimer will not apply to the extent prohibited by
            applicable law.
          </p>
          <p>The foregoing limitations:</p>
          <ul>
            <li>
              will apply whether such damages arise out of breach of contract,
              tort (including negligence) or otherwise and regardless of whether
              such damages were foreseeable or SatoshiEye was advised of the
              possibility of such damages; and
            </li>
            <li>
              include any loss caused by the failure of the Services to
              correctly identify participants in blockchain transactions or the
              levels of any associated risks such as fraudulent activity, and
              you acknowledge and agree that in entering into these Terms you do
              not rely on the Services for such purposes.
            </li>
          </ul>

          <div class="bold-600 fs-24 mb-3">
            Indemnification
          </div>
          <p>
            Customer agrees to indemnify, defend and hold harmless SatoshiEye
            and its officers, directors, employees, agents, contractors,
            affiliates, successors and assigns from and against any and all
            third-party claims, damages, liabilities, costs, and expenses,
            including reasonable attorneys' fees, arising from or relating to
            (i) Customer's breach of these Terms; or (ii) Customer's use of the
            Services in violation of applicable law. Furthermore, you agree that
            SatoshiEye assumes no responsibility for the content you submit or
            make available through the Services.
          </p>
          <p>
            In the event of any claim, action, or proceeding brought against
            SatoshiEye related to your use of the Services or your breach of
            these Terms, you agree to cooperate fully with SatoshiEye in the
            defense of such claim, action, or proceeding. SatoshiEye reserves
            the right, at its own expense, to assume the exclusive defense and
            control of any matter subject to indemnification by you.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Confidentiality
          </div>
          <p>
            SatoshiEye will use reasonable measures to protect confidential
            information submitted by Customers through the Services, subject to
            these Terms, the
            <a
              :href="privacyPolicyUrl"
              rel="noopener noreferrer"
              target="_blank"
            >Privacy Policy</a>, and the nature of blockchain-related data. "Confidential
            Information" includes any and all information received or acquired
            by you from SatoshiEye in pursuance of these Terms, in whatever
            form, before or after the effective date hereof, directly or
            indirectly, orally, in any written form, or in any magnetically or
            electronically recorded form or another form, whether or not stored,
            compiled or memorialized in any media or in writing, including,
            without limitation, information and materials pertaining to party's
            business, technologies, codes, source codes, products, intellectual
            property, developments, finances, operations, processes, regulatory
            information, analysis, ideas, concepts, trade secrets, know-how,
            technical information, inventions, business plans, products,
            samples, product information, research, drawings, algorithms,
            software codes and designs, physical material, customers, employees
            and consultants, contractors, suppliers, marketing plans, and other
            proprietary and commercial information, shall not be disclosed to
            any third party by the recipient except as provided in these Terms
            or any other written agreement between the parties.
          </p>
          <p>
            You shall use a reasonable degree of care to maintain all
            Confidential Information in confidence and shall not disclose to any
            third-party nor use Confidential Information for any unauthorised
            purpose. You may only disclose Confidential Information to those of
            Your employees and representatives that have both (i) a need to know
            for your internal purposes in configuring, installing, using, and
            supporting the Services and (ii) are legally bound by
            confidentiality obligations no less stringent than those of this
            Agreement.
          </p>
          <p>
            The provisions of this clause shall not apply to any information
            which:
          </p>
          <p>
            (a) is in, or later comes into, the public domain otherwise than by
            breach of these Terms or any other agreement between the parties;
            <br>(b) was demonstrably in the possession of the recipient prior
            to its receipt from the other party; <br>(c) is independently
            received from a third party who is free from any obligations to a
            third party not to disclose it; <br>(d) is demonstrably conceived
            by the recipient independently of the information received or
            acquired from the other party; or <br>(e) the recipient is bound
            by applicable laws or regulations to disclose.
          </p>
          <p>
            The provisions of this clause shall survive termination of these
            Terms for any reason.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Term and Termination
          </div>
          <p>
            These Terms shall remain in effect until terminated as set forth in
            this section.
          </p>
          <p>
            SatoshiEye may terminate these Terms at any time with reasonable
            advance notice for any reason including if SatoshiEye ceases to
            support the Services, which SatoshiEye may do at its sole
            discretion. Other reasons for termination include: (i) an allegation
            or actual infringement of any Intellectual Property Right; (ii) an
            allegation of or actual defamation; (iii) an allegation or
            determination that the Services do not comply with applicable law;
            (iv) SatoshiEye ceasing to do business; or (v) SatoshiEye filing a
            petition in bankruptcy, dissolving, or otherwise finding itself
            unable to pay its debts as they come due. If SatoshiEye terminates a
            Subscription for convenience, SatoshiEye may provide a pro-rated
            refund for the unused portion of the then-current paid term, except
            where the termination results from the Customer’s breach, fraud,
            unlawful conduct, non-payment,
          </p>
          <p>
            SatoshiEye may terminate these Terms immediately without any notice
            if you breach any of the terms and conditions of these Terms.
          </p>
          <p>
            A Service may be removed from the SatoshiEye Website at any time if
            it is determined that the Service (i) may infringe or otherwise
            infringe the Intellectual Property Rights or any other rights of any
            third party; (ii) violates any applicable law, regulation; or (iii)
            is subject to an injunction.
          </p>
          <p>
            Upon any termination of these Terms: (i) all rights granted to
            Customer under these Terms shall terminate the effective date of
            termination; (ii) Customer shall cease all use of the Services and
            destroy any copies of the Services in its possession; (iii) any
            right to generate new Reports, request data refreshes, or access
            paid outputs will end as described in these Terms, (iv) no unused
            Included Usage, legacy token balance, or similar allowance converts
            into cash, stored value, or account credit unless expressly required
            by applicable law or expressly stated by SatoshiEye. The following
            sections shall survive any termination of these Terms: Intellectual
            Property Ownership, Confidentiality, Limitation of Liability,
            Indemnification, Governing Law, and Miscellaneous.
          </p>
          <p>
            Termination will not limit any of SatoshiEye's rights or remedies at
            law or in equity.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Privacy Rights
          </div>
          <p>
            Your privacy rights, including rights of access, correction,
            deletion, objection, withdrawal of consent where applicable, and
            marketing opt-out, are described in the
            <a
              :href="privacyPolicyUrl"
              rel="noopener noreferrer"
              target="_blank"
            >Privacy Policy</a>.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Billing notices and Communication Between Us
          </div>
          <p>
            SatoshiEye will send transactional subscription notices required by
            applicable payment-network rules and law, including subscription
            confirmations, trial or promotional-period reminders, renewal
            reminders for longer billing cycles, material-change notices,
            receipts, failed-payment notices, and cancellation confirmations.
            These notices will be sent by email using the contact details
            associated with the Account.
          </p>
          <p>
            If you wish to contact SatoshiEye in writing, or if any condition in
            these Terms requires you to give us notice in writing, you can send
            this to us by e-mail to
            <a
              href="mailto:support@satoshieye.com"
              rel="noopener noreferrer"
              target="_blank"
            >support@satoshieye.com</a>.
          </p>

          <div class="bold-600 fs-24 mb-3">
            Miscellaneous
          </div>
          <p>
            1. Transfer <br>SatoshiEye may transfer its rights and obligations
            under these Terms to another organization in case of a merger or
            acquisition with such another organization. You may only transfer
            your rights or obligations under these Terms upon SatoshiEye's prior
            written consent.
          </p>
          <p>
            2. Severability <br>If any provision of these Terms is illegal or
            unenforceable under applicable law, the remainder of the provision
            will be amended to achieve as closely as possible the effect of the
            original term, and all other provisions of these Terms will continue
            in full force and effect.
          </p>
          <p>
            3. Governing Law &amp; Jurisdiction <br>The Terms and any disputes
            or claims arising out of or in connection with it or its subject
            matter or formation (including without limitation disputes or
            claims) are governed by and construed in accordance with the laws of
            Switzerland. Any dispute, controversy, or claim arising out of or
            relating to the Terms, or the breach, termination, or invalidity
            thereof, shall be referred to and finally resolved by an Arbitration
            tribunal in accordance with the Swiss Rules of International
            Arbitration of the Swiss Arbitration Centre in force on the date on
            which the Notice of Arbitration is submitted in accordance with
            those Rules (the "Swiss Rules"). The seat of the arbitration shall
            be Zug, Switzerland (unless the parties agree in writing on a city
            in another country); The arbitral proceedings shall be conducted in
            English language before one (1) arbitrator appointed in accordance
            with the Swiss Rules.
          </p>
          <p>
            If Customer is a Consumer, the arbitration and jurisdiction
            provisions apply only to the extent permitted by applicable law and
            do not deprive Customer of any mandatory right to bring a claim
            before a competent court.
          </p>
          <p>
            4. Entire Agreement <br>These Terms constitute the entire
            agreement between you and SatoshiEye with respect to the Services
            and supersedes all prior or contemporaneous understandings and
            agreements, whether written or oral, with respect to the Services.
          </p>
          <p>
            5. Waiver <br>No failure to exercise and no delay in exercising,
            on the part of either party, any right or any power hereunder shall
            operate as a waiver thereof, nor shall any single or partial
            exercise of any right or power hereunder preclude further exercise
            of that or any other right hereunder. In the event of a conflict
            between these Terms and any applicable purchase or other terms, the
            terms of these Terms shall govern.
          </p>
          <p>
            6. Export Regulation <br>The Services may be subject to the laws
            related to export control, economic or financial sanctions and/or
            trade embargoes of the including but not limited to the US Export
            Administration Act. Without limiting the generality of the
            foregoing, you shall not, directly or indirectly, export, re-export,
            or release the Services to, or make the Services accessible from,
            any jurisdiction or country to which export, re-export, or release
            is prohibited by applicable law, rule, or regulation. You shall
            comply with all applicable federal laws, regulations, and rules, and
            complete all required undertakings (including obtaining any
            necessary export license or other governmental approval) and you
            shall not export, re-export, release, or otherwise make the Services
            available, without obtaining all required governmental licenses or
            other authorizations.
          </p>
          <p>
            7. Force Majeure <br>Notwithstanding any other provision of these
            Terms, SatoshiEye shall not be deemed in default of these Terms for
            failure to fulfil its obligations when due to causes beyond its
            reasonable control.
          </p>
          <p>
            8. Collection of Information <br>SatoshiEye may use cookies and
            similar technologies as described in the
            <a
              :href="cookiePolicyUrl"
              rel="noopener noreferrer"
              target="_blank"
            >Cookie Policy</a>
            to operate the Services, improve functionality, measure performance,
            and where permitted by applicable law, support communications and
            marketing. You also may be required to provide certain information
            about yourself as a condition to use certain features or
            functionality of the Services. All information we collect through or
            in connection with the Services is subject to our
            <a
              :href="privacyPolicyUrl"
              rel="noopener noreferrer"
              target="_blank"
            >Privacy Policy</a>. By using the Services and providing information through them, you
            acknowledge that your personal data will be handled in accordance
            with the
            <a
              :href="privacyPolicyUrl"
              rel="noopener noreferrer"
              target="_blank"
            >Privacy Policy</a>.
          </p>
          <p>9. Publicity for Business Customers</p>
          <p>
            This section applies only to Customers acting in a business or
            professional capacity.
          </p>
          <p>
            Unless otherwise agreed in writing, SatoshiEye may identify the
            Business Customer as a user of the Services and may use the Business
            Customer’s name, trade name, trademark, or logo in customer lists,
            website references, and general marketing materials solely for the
            purpose of identifying the Business Customer as a customer of
            SatoshiEye.
          </p>
          <p>
            The Business Customer may opt out of such use at any time by
            providing written notice to SatoshiEye. SatoshiEye will use
            reasonable efforts to discontinue future use within a reasonable
            period.
          </p>
          <p>
            Any detailed case study, testimonial, endorsement, or public
            reference describing the Business Customer’s specific use of the
            Services will require the Business Customer’s prior written
            approval.
          </p>
          <p>10. No Endorsement</p>
          <p>
            Any reference to a Customer does not imply endorsement of
            SatoshiEye’s Services unless explicitly agreed in writing.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TermsOfUse',
  computed: {
    websiteUrl() {
      return 'https://satoshieye.com/'
    },
    privacyPolicyUrl() {
      return this.$router.resolve({ name: 'privacy-policy' }).href
    },
    cookiePolicyUrl() {
      return this.$router.resolve({ name: 'cookie-policy' }).href
    },
  },
}
</script>
